import React, { useState, useEffect } from 'react';
import DescuentoTardanzaService from '../axios_services/descuentotardanza.service';
import Loader from '../components/Loader/Loader';
import ModalDescuentoOtros from '../components/Modal/ModalDescuentoOtros';
import { ButtonSearch } from '../components/Buttons/Buttons';
import { Container, Table } from 'reactstrap';
import './DescuentoTardanza.css';

const fmt = (v) =>
    new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(v ?? 0);

// ─── Componente principal ─────────────────────────────────────────────────────
function DescuentoTardanza() {
    const [state] = React.useContext(require('../application/provider').AppContext);
    const [isLoading, setIsLoading] = useState(true);

    const allowedEditors = [5, 8, 22, 26];
    const canEdit = allowedEditors.includes(Number(state.user?.perfil_id));

    const [mes, setMes] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const [registros, setRegistros] = useState([]);
    const [area, setArea] = useState('');
    const [status, setStatus] = useState('1');
    const [modalOtrosOpen, setModalOtrosOpen] = useState(false);
    const [selectedRegistro, setSelectedRegistro] = useState(null);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async (paramMes = mes, paramArea = area, paramStatus = status) => {
        setIsLoading(true);
        try {
            const res = await DescuentoTardanzaService.getResumen(paramMes, paramArea, paramStatus);
            setRegistros(res.data || []);
        } catch (err) { console.error(err); }
        finally {
            setIsLoading(false);
        }
    };

    const totalDesc = registros.reduce((acc, r) => acc + Number(r.total_descuento), 0);

    return (
        <Container className="descuentos-planilla-container">
            {isLoading && <Loader />}
            <div className="encabezado">
                <h1>DESCUENTOS PLANILLA</h1>
            </div>

            <div className="filtros">
                <div>
                    <label>Mes:</label>
                    <input
                        type="month"
                        value={mes}
                        onChange={e => setMes(e.target.value)}
                    />
                </div>
                <div>
                    <label>Área:</label>
                    <select
                        value={area}
                        onChange={e => setArea(e.target.value)}
                        className="form-control-sm border border-gray-300 rounded px-2"
                    >
                        <option value="">Todos</option>
                        <option value="1">Administración</option>
                        <option value="2">Negocios</option>
                        <option value="4">Gerencia</option>
                    </select>
                </div>
                <div>
                    <label>Estado:</label>
                    <select
                        value={status}
                        onChange={e => setStatus(e.target.value)}
                        className="form-control-sm border border-gray-300 rounded px-2"
                    >
                        <option value="1">Activos</option>
                        <option value="0">Cesados</option>
                    </select>
                </div>
            </div>

            <div className="mt-2">
                <ButtonSearch onClick={() => loadData(mes, area, status)} isLoading={isLoading} />
            </div>

            <div className={`mt-3`}>
                <div className="table-container">
                    <Table hover size="sm">
                        <thead>
                            <tr>
                                <th rowSpan={2}>COLABORADOR</th>
                                <th rowSpan={2}>CARGO</th>
                                <th rowSpan={2}>SUELDO</th>
                                <th colSpan={2}>TARDANZA</th>
                                <th colSpan={2}>INASISTENCIAS (½ DÍAS)</th>
                                <th colSpan={2}>INASISTENCIAS (DÍAS)</th>
                                <th colSpan={2}>OTROS</th>
                                <th rowSpan={2}>TOTAL DESC.</th>
                            </tr>
                            <tr>
                                <th>Minutos</th>
                                <th>Descuento</th>
                                <th>Cantidad</th>
                                <th>Descuento</th>
                                <th>Cantidad</th>
                                <th>Descuento</th>
                                <th>Descripción</th>
                                <th>Descuento</th>
                            </tr>
                        </thead>
                        <tbody>
                            {registros.length === 0 ? (
                                <tr>
                                    <td colSpan={12} className="text-center py-3">Sin registros para el mes seleccionado</td>
                                </tr>
                            ) : registros.map((r) => (
                                <tr key={r.usuario_id}>
                                    <td style={{ textAlign: 'left' }}>{r.usuario_nombre?.toUpperCase()}</td>
                                    <td>{r.perfil_nombre}</td>
                                    <td>{fmt(r.sueldo_bruto)}</td>

                                    {/* TARDANZA — solo lectura */}
                                    <td className="text-center">
                                        <span className="text-[10px] text-gray-700">{r.tol_cantidad ?? 0}</span>
                                    </td>
                                    <td className="text-center">
                                        {r.tol_monto > 0 ? fmt(r.tol_monto) : '-'}
                                    </td>

                                    {/* INASISTENCIAS ½ DÍA — solo lectura */}
                                    <td className="text-center">
                                        <span className="text-[10px] text-gray-700">{r.tar_cantidad ?? 0}</span>
                                    </td>
                                    <td className="text-center">
                                        {r.tar_monto > 0 ? fmt(r.tar_monto) : '-'}
                                    </td>

                                    {/* FALTAS — solo lectura */}
                                    <td className="text-center">
                                        <span className="text-[10px] text-gray-700">{r.fal_cantidad ?? 0}</span>
                                    </td>
                                    <td className="text-center">
                                        {r.fal_monto > 0 ? fmt(r.fal_monto) : '-'}
                                    </td>

                                    {/* OTROS — editable */}
                                    <td>
                                        <div className="relative flex items-center justify-between min-w-[100px] mx-auto">
                                            <span
                                                className="text-[10px] text-gray-700 truncate max-w-[100px]"
                                                title={r.otros_descripciones !== '-' ? r.otros_descripciones : 'Sin detalles'}
                                            >
                                                {r.otros_descripciones}
                                            </span>
                                            {canEdit && (
                                                <button
                                                    type="button"
                                                    className="text-gray-500 hover:text-blue-600 focus:outline-none p-0.5 rounded transition-colors ml-1"
                                                    onClick={() => { setSelectedRegistro(r); setModalOtrosOpen(true); }}
                                                >
                                                    <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 9-7 7-7-7" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        {r.otros_monto > 0 ? fmt(r.otros_monto) : '-'}
                                    </td>

                                    {/* TOTAL */}
                                    <td className="text-center">
                                        {fmt(r.total_descuento)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        {registros.length > 0 && (
                            <tfoot>
                                <tr>
                                    <td colSpan={11} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                        TOTAL DESCUENTOS DEL MES:
                                    </td>
                                    <td style={{ fontWeight: 'bold' }}>
                                        {fmt(totalDesc)}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </Table>
                </div>
            </div>

            <ModalDescuentoOtros
                isOpen={modalOtrosOpen}
                onClose={() => { setModalOtrosOpen(false); setSelectedRegistro(null); }}
                onSaved={() => loadData()}
                registro={selectedRegistro}
                mes={mes}
            />
        </Container>
    );
}

export default DescuentoTardanza;
